import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import Textarea from 'components/Textarea/Textarea';
import TextField from 'components/TextField/TextField';
import {
  CONTACT_EMAIL,
  CONTACT_NAME,
  INTERNAL_LIAISON,
  INTERNAL_LIAISON_EMAIL,
  MEDIA_ORGANISATION_NAME,
  RELEVANCE_TO_COMPANY,
  REPRESENTATION_CATEGORY,
} from 'constants/formFields';
import { DEFAULT_MAX_LIMITED, OPTION_CATEGORY_NOGS } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHMediaMutation, useUpdateMaterialitySHMediaMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import RowLiaison from './components/RowLiaison';
import useCreateMediaForm from './hooks/useCreateMediaForm';

import type IMaterialSHMediaItem from 'store/slices/materialAssessment/interfaces/IMaterialSHMediaItem';
import type IFieldError from 'store/types/IFieldError';
import type { TMediaCreateSchema } from 'utils/validators';
import type ICreateMedia from './interfaces/ICreateMedia';

import styles from './scss/CreateMedia.module.scss';

function CreateMedia({
  open, onClose, title, selectedItem, recordId, setMedia, currentId, setOpenModal,
  vcmId,
}: ICreateMedia) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createEmployees] = useCreateMaterialitySHMediaMutation();
  const [updateEmployees] = useUpdateMaterialitySHMediaMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateMediaForm({ isPageLoading: isLoading, selectedItem });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddMedia = (item: IMaterialSHMediaItem) => {
    if (currentId !== -1) {
      updateEmployees({
        id: currentId, maId: recordId, data: item, vcmId,
      }).then((request) => {
        toast.success(i18n.t('notifications.updatedMedia'));
        if ('data' in request && request.data?.data) {
          setMedia((prev) => {
            const index = prev.findIndex((itemIndex) => itemIndex.id === currentId);
            if (index !== -1) {
              const updatedMedia = [...prev];
              updatedMedia[index] = { ...updatedMedia[index], ...request.data.data };
              return updatedMedia;
            }
            return prev;
          });
        }
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TMediaCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createEmployees({
        data: item,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdMedia'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TMediaCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateMediaSubmit = handleSubmit((data) => {
    onAddMedia({
      name: data[MEDIA_ORGANISATION_NAME],
      representationCategory: data[REPRESENTATION_CATEGORY]?.value || '',
      relevance: data[RELEVANCE_TO_COMPANY] || '',
      contactName: data[CONTACT_NAME] || '',
      contactEmail: data[CONTACT_EMAIL] || '',
      internalLiaison: data[INTERNAL_LIAISON] || '',
      internalLiaisonEmail: data[INTERNAL_LIAISON_EMAIL] || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <div className={styles.lColumn}>
            <Controller
              control={control}
              name={MEDIA_ORGANISATION_NAME}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <TextField
                  name={name}
                  value={value}
                  label={t('materialityAssessment.fields.mediaOrganisationName')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  required
                  placeholder={t('materialityAssessment.placeholder.typeHere')}
                />
              )}
            />
          </div>

          <div className={styles.rColumn}>
            <Controller
              control={control}
              name={REPRESENTATION_CATEGORY}
              render={({
                field: {
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <Select
                  label={t('materialityAssessment.fields.representationCategory')}
                  placeholder={t('materialityAssessment.placeholder.selectDepartment')}
                  options={OPTION_CATEGORY_NOGS}
                  value={value?.value ? value : undefined}
                  onChange={onChange}
                  error={(error as IFieldError)?.value?.message}
                  modalRef={modalRef}
                  menuPosition="absolute"
                  required
                />
              )}
            />
          </div>
          <div className={styles.fullWidth}>
            <Controller
              control={control}
              name={RELEVANCE_TO_COMPANY}
              render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <Textarea
                  name={name}
                  value={value}
                  label={t('materialityAssessment.fields.relevanceToCompany')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder={t('materialityAssessment.placeholder.typeHere')}
                  charactersLimit={DEFAULT_MAX_LIMITED}
                  required
                />
              )}
            />
          </div>
          <div className={styles.lColumn}>
            <Controller
              control={control}
              name={CONTACT_NAME}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <TextField
                  name={name}
                  value={value}
                  label={t('materialityAssessment.fields.contactName')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  required
                  placeholder={t('materialityAssessment.placeholder.typeHere')}
                />
              )}
            />
          </div>
          <div className={styles.rColumn}>
            <Controller
              control={control}
              name={CONTACT_EMAIL}
              render={({
                field: {
                  name,
                  value,
                  onChange,
                },
                fieldState: { error },
              }) => (
                <TextField
                  name={name}
                  value={value}
                  label={t('materialityAssessment.fields.contactEmail')}
                  error={error?.message}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder={t('materialityAssessment.placeholder.typeHere')}
                  required
                />
              )}
            />
          </div>
          <RowLiaison control={control} />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateMediaSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateMedia;
